import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, TrendingUp, BarChart, FileText,
  ArrowRightLeft, ChevronRight, CheckCircle2, UserCog,
  Network, ClipboardCheck, Send, BriefcaseBusiness,
  PhoneCall,
} from "lucide-react";
import { CheckAdmin } from "../../../../Auth/Services/StartSession";
import Cookies from "js-cookie";
import NotificationAlert from "../../../../CustomAlerts/NotificationAlert";

const Sidebar = () => {
  const [activeMenu, setActiveMenu] = useState(null);
  const sidebarRef = useRef(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [alert, setAlert] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const userCookie = Cookies.get("user");
      if (userCookie) {
        const user = JSON.parse(userCookie);
        setIsAdmin(user?.isAdmin || false);
        setIsManager(user?.isManager || false);
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setActiveMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const menus = {
    dashboard: [
      {
        label: "Home",
        path: "/dashboard",
        icon: <LayoutDashboard size={14} />,
      },
    ],

    leads: [
      {
        section: "My Work",
        items: [
          {
            label: "My Leads",
            path: "/my-leads",
            icon: <FileText size={14} />,
          },
          {
            label: "Raised Requests",
            path: "/my-lead-transfer-requests",
            icon: <Send size={14} />,
          },
          {
            label: "Pending Requests",
            path: "/lead-transfer-requests",
            icon: <ArrowRightLeft size={14} />,
          },
          {
            label: "Resolved Requests",
            path: "/lead-transfer-resolved",
            icon: <CheckCircle2 size={14} />,
          },
        ],
      },

      ...(isManager
        ? [
            {
              section: "Team",
              items: [
                {
                  label: "Team Leads",
                  path: "/team-leads",
                  icon: <Network size={14} />,
                },
                {
                  label: "Team Transfer Requests",
                  path: "/team-transfer-request",
                  icon: <ClipboardCheck size={14} />,
                },
                {
                  label: "Resolved Team Requests",
                  path: "/team-transfer-resolved",
                  icon: <CheckCircle2 size={14} />,
                },
              ],
            },
          ]
        : []),
    ],

    deals: [
      {
        section: "My Work",
        items: [
          {
            label: "My Deals",
            path: "/deals/my-deals",
            icon: <BriefcaseBusiness size={14} />
          }
        ]
      }
    ],

    report: [
      {
        label: "Call Logs",
        path: "/call-logs",
        icon: <PhoneCall size={14} />,
      }
    ],
  };

  const handleMenuClick = (menu) => {
    setActiveMenu((prev) => (prev === menu ? null : menu));
  };

  const handleAdminClick = async () => {
    try {
      const res = await CheckAdmin();
      if (res?.status?.toLowerCase() === "success" && res?.isAdmin) {
        navigate("/admin/dashboard");
      } else {
        return;
      }
    } catch (error) {
      setAlert({
        type: "error",
        message: "You are not authorized",
      });
    }
  };

  return (
    <>
      <div ref={sidebarRef} className="flex h-screen sticky top-0">
        {/* LEFT ICON SIDEBAR */}

        <div className="w-20 flex flex-col items-center py-3 bg-gradient-to-b from-[#DFF4FF] via-[#EAF7FF] to-[#E3F5FF] border-r border-[#CFEFFF]">
          <SidebarMenuButton
            active={activeMenu === "dashboard"}
            icon={<LayoutDashboard size={20} />}
            label="Dashboard"
            onClick={() => handleMenuClick("dashboard")}
          />

          <SidebarMenuButton
            active={activeMenu === "leads"}
            icon={<Users size={20} />}
            label="Leads"
            onClick={() => handleMenuClick("leads")}
          />

          <SidebarMenuButton
            active={activeMenu === "deals"}
            icon={<TrendingUp size={20} />}
            label="Deals"
            onClick={() => handleMenuClick("deals")}
          />

          <SidebarMenuButton
            active={activeMenu === "report"}
            icon={<BarChart size={20} />}
            label="Report"
            onClick={() => handleMenuClick("report")}
          />

          {isAdmin && (
            <SidebarMenuButton
              icon={<UserCog size={20} />}
              label="Go to Admin"
              onClick={handleAdminClick}
            />
          )}
        </div>

        {/* RIGHT SUB MENU PANEL */}

        <div className={`transition-all duration-300 overflow-hidden ${activeMenu ? "w-52 opacity-100" : "w-0 opacity-0"}`}>
          {activeMenu && (
            <div className="h-full bg-white border-r border-slate-200 px-3 py-4">
              {/* TITLE */}
              <h2 className="text-[20px] font-semibold text-slate-800 capitalize mb-3">
                {activeMenu}
              </h2>

              <div className="space-y-1">
                {["leads", "deals"].includes(activeMenu)
                  ? menus[activeMenu].map((section) => (
                      <div key={section.section}>
                        <div className="px-3 pt-3 pb-2">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                            {section.section}
                          </p>

                          <div className="mt-2 h-px bg-slate-200" />
                        </div>

                        <div className="space-y-1">
                          {section.items.map((item) => (
                            <NavLink
                              key={item.path}
                              to={item.path}
                              onClick={() => setActiveMenu(null)}
                              className={({ isActive }) =>
                                `flex items-center gap-3 rounded-2xl px-3 py-2 transition-all duration-200
                                  ${
                                    isActive
                                      ? "bg-sky-50 text-sky-600 font-medium shadow-sm"
                                      : "text-slate-600 hover:bg-slate-100"
                                  }`
                              }
                            >
                              <div className="h-7 w-7 rounded-xl bg-sky-50 flex items-center justify-center text-sky-600">
                                {item.icon}
                              </div>

                              <span className="text-[13px] whitespace-nowrap">
                                {item.label}
                              </span>
                            </NavLink>
                          ))}
                        </div>
                      </div>
                    ))
                  : menus[activeMenu].map((item, index) => (
                      <NavLink
                        key={index}
                        to={item.path}
                        onClick={() => setActiveMenu(null)}
                        className={({ isActive }) =>
                          `flex items-center gap-3 rounded-2xl px-3 py-2 transition-all duration-200
                            ${
                              isActive
                                ? "bg-sky-50 text-sky-600 font-medium shadow-sm"
                                : "text-slate-600 hover:bg-slate-100"
                            }`
                        }
                      >
                        <div className="h-7 w-7 rounded-xl bg-sky-50 flex items-center justify-center text-sky-600">
                          {item.icon}
                        </div>

                        <span className="text-[13px]">{item.label}</span>
                      </NavLink>
                    ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {alert && (
        <NotificationAlert
          show={!!alert}
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

/* LEFT MENU BUTTON */

const SidebarMenuButton = ({ active, icon, label, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`w-14 rounded-xl flex flex-col items-center py-3 mb-2 transition-all duration-300
                  ${active ? "text-sky-600" : "text-slate-600 hover:bg-white/70"}
                `}
    >
      <div
        className={`rounded-xl p-1.5 backdrop-blur-sm transition-all duration-300  ${active ? "bg-white shadow-sm" : "bg-white/80"}`}
      >
        {icon}
      </div>

      <span className="text-[10px] mt-1.5 font-medium leading-tight">
        {label}
      </span>
    </button>
  );
};

export default Sidebar;
